import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeroSection from './HeroSection';
import TrustedBySection from './TrustedBySection';
import '../styles/HomePage.css';
import OurServicesSection from './OurServices';
import { Boxes } from 'lucide-react';
import TechStackSection from './industries/TechStackSection';


const HomePage: React.FC = () => {
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <HeroSection />
      <TrustedBySection />
      <div className="home-page-h">
        <OurServicesSection />
        <div className="p-8 md:p-12">
        <div className="hero-container-h">
          <div className="hero-video-section-h">
            <video className="hero-background-video-h" autoPlay muted loop>
            <source src="https://www.marketing.webmobrilinc.com/wp-content/themes/hello-elementor/portfolio/images/videos/welcome-3.mp4" type="video/mp4" />
            Your browser does not support the video tag.
            </video>
            <div className="hero-video-overlay-h">
              <h1 className="hero-heading-h">
                <span className="heading-highlight-h">Powering</span> Next-Gen Cloud Innovations
              </h1>
            </div>
            <div className="page-fold-effect"></div>
          </div>
          <div className="hero-content-h">
            <p className="hero-subtext-h">
              Leveraging both <strong>server-based</strong> and <strong>serverless architectures</strong>, real-time streaming analytics,
              edge-native IoT frameworks, AI/ML pipelines, and
              blockchain composability, we deliver
              <span className="highlight-text"> scalable, secure solutions</span> for sectors like fintech, autonomous mobility, and decentralized
              applications.
            </p>
            <div className="tech-pill-container-h">
              <span className="tech-pill-h">Serverless</span>
              <span className="tech-pill-h">IoT</span>
              <span className="tech-pill-h">AI/ML</span>
              <span className="tech-pill-h">Blockchain</span>
            </div>
          </div>
          <div className="hero-animation-h">
            <div className="particles-h"></div>
            <div className="circuit-lines-h"></div>
          </div>
        </div>
        </div>
        <section className="home-section-h home-industries-section-h">
          <div className="home-section-header-h">
            <h2 className="home-section-title-h">Industries We Serve</h2>
            <p className="home-section-subtitle-h">Tailored solutions for diverse sectors</p>
          </div>

          <div className="home-industries-grid-h">
            <Link to="/industries/healthcare-life-sciences" className="home-industry-item-h">
              <div className="home-industry-icon-h">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                </svg>
              </div>
              <h3 className="home-industry-name-h">Healthcare</h3>
              <p className="home-industry-description-h">Digital health solutions, medical data management, and healthcare infrastructure modernization</p>
            </Link>

            <Link to="/industries/finance-banking" className="home-industry-item-h">
              <div className="home-industry-icon-h">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
              </div>
              <h3 className="home-industry-name-h">Fintech</h3>
              <p className="home-industry-description-h">Payment processing, digital banking, and financial technology infrastructure</p>
            </Link>

            <Link to="/industries/machine-learning-ai" className="home-industry-item-h">
              <div className="home-industry-icon-h">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a8 8 0 0 0-8 8c0 1.892.402 3.13 1.5 4.5L12 22l6.5-7.5c1.098-1.37 1.5-2.608 1.5-4.5a8 8 0 0 0-8-8z"></path>
                </svg>
              </div>
              <h3 className="home-industry-name-h">AI/ML & Data Science</h3>
              <p className="home-industry-description-h">Machine learning infrastructure, data analytics, and AI-powered solutions</p>
            </Link>

            <div className="home-industry-item-h">
              <div className="home-industry-icon-h">
               <Boxes size={24} strokeWidth={1} className="home-industry-icon-h" />
              </div>
              <h3 className="home-industry-name-h">Blockchain</h3>
              <p className="home-industry-description-h">Distributed ledger technology, smart contracts, and decentralized applications</p>
            </div>

            <Link to="/industries/saas" className="home-industry-item-h">
              <div className="home-industry-icon-h">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <h3 className="home-industry-name-h">SaaS & Digital Platforms</h3>
              <p className="home-industry-description-h">Cloud-based software solutions and scalable digital platforms</p>
            </Link>

            <Link to="/industries/retail-ecommerce" className="home-industry-item-h">
              <div className="home-industry-icon-h">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
              </div>
              <h3 className="home-industry-name-h">Retail</h3>
              <p className="home-industry-description-h">E-commerce solutions, inventory management, and retail technology infrastructure</p>
            </Link>
          </div>

          <div className="home-section-footer-h">
            <Link to="/industries" className="home-view-all-button-h">
              See All Industries
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </Link>
          </div>
        </section>

        <TechStackSection 
          title={`Our Engineering Stack Comes with your security needs`} 
          description={`Betaque ensures your infrastructure isn't just ready but ready for any compliant that you need`}
          items={[]} 
        />

        {/* Why Choose Us Section */}
        <section className="home-section-h why-us-section-h">
          <div className="why-us-header-h">
            <h2 className="why-us-title-h">Why Choose Betaque?</h2>
          </div>
          <div className="why-us-container-h">
            <div className="why-us-features-grid-h">
              {[
                {
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="7"></circle>
                      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                    </svg>
                  ),
                  title: "Expert Team",
                  description: "Proven track record with years of industry experience"
                },
                {
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                  ),
                  title: "Security Focus",
                  description: "Prioritizing security and compliance in every solution"
                },
                {
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                  ),
                  title: "Innovation",
                  description: "Creative approach to solving complex problems"
                },
                {
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"></path>
                    </svg>
                  ),
                  title: "Client Success",
                  description: "Committed to delivering value and exceeding expectations"
                }
              ].map((feature, index) => (
                <div className="feature-card-h" key={index}>
                  <div className="feature-icon-wrapper-h">
                    {feature.icon}
                  </div>
                  <h3 className="feature-title-h">{feature.title}</h3>
                  <p className="feature-description-h">{feature.description}</p>
                </div>
              ))}
            </div>
            
            <div className="why-us-stats-bar-h">
              {[
                { number: "12+", label: "Years Experience" },
                { number: "200+", label: "Successful Projects" },
                { number: "98%", label: "Client Satisfaction" }
              ].map((stat, index) => (
                <div className="stat-item-h" key={index}>
                  <div className="stat-number-h">{stat.number}</div>
                  <div className="stat-label-h">{stat.label}</div>
                </div>
              ))}
            </div>
            
            <div className="why-us-footer-h">
              <Link to="/why-us" className="learn-more-button-h">
                Learn More
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;